import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Mail, Phone, Calendar, Ruler, Target, FileText, TrendingUp, Edit3, Save, X, Camera, Scale } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { formatDateBR } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPerfilPaciente = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [previewFoto, setPreviewFoto] = useState(null);
  const [evolutionTable, setEvolutionTable] = useState([]);
  const [savingEvolution, setSavingEvolution] = useState(false);

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  // Initialize evolution table based on treatment duration (4 doses per month)
  useEffect(() => {
    if (patientData?.patient) {
      const duration = patientData.patient.duracao_tratamento || 6;
      const totalDoses = duration * 4; // 4 doses per month
      const existingData = patientData.evolution_table || [];
      
      // Create table with correct number of columns
      const newTable = [];
      for (let i = 1; i <= totalDoses; i++) {
        const existing = existingData.find(e => e.dose_num === i) || {};
        newTable.push({
          dose_num: i,
          dose_mg: existing.dose_mg || '',
          data_aplicacao: existing.data_aplicacao || '',
          peso: existing.peso || '',
          med_quadril: existing.med_quadril || '',
          med_abdome: existing.med_abdome || ''
        });
      }
      setEvolutionTable(newTable);
    }
  }, [patientData]);

  const fetchPatientData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/patients/${patientId}`);
      setPatientData(response.data);
      setEditData(response.data.patient);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do paciente');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A foto deve ter no máximo 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewFoto(reader.result);
        setEditData({ ...editData, foto_perfil: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        nome: editData.nome,
        email: editData.email,
        telefone: editData.telefone || null,
        data_nascimento: editData.data_nascimento || null,
        altura: editData.altura ? parseFloat(editData.altura) : null,
        peso_inicial: editData.peso_inicial ? parseFloat(editData.peso_inicial) : null,
        peso_atual: editData.peso_atual ? parseFloat(editData.peso_atual) : null,
        peso_meta: editData.peso_meta ? parseFloat(editData.peso_meta) : null,
        duracao_tratamento: editData.duracao_tratamento ? parseInt(editData.duracao_tratamento) : null,
        historico_medico: editData.historico_medico || null,
        foto_perfil: editData.foto_perfil || null,
        cintura_inicial: editData.cintura_inicial ? parseFloat(editData.cintura_inicial) : null,
        quadril_inicial: editData.quadril_inicial ? parseFloat(editData.quadril_inicial) : null,
        peito_inicial: editData.peito_inicial ? parseFloat(editData.peito_inicial) : null,
        braco_inicial: editData.braco_inicial ? parseFloat(editData.braco_inicial) : null,
        perna_inicial: editData.perna_inicial ? parseFloat(editData.perna_inicial) : null,
      };

      await axios.put(`${API_URL}/api/admin/patients/${patientId}`, dataToSave);
      toast.success('Perfil atualizado com sucesso!');
      setIsEditing(false);
      setPreviewFoto(null);
      fetchPatientData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleEvolutionChange = (index, field, value) => {
    const newTable = [...evolutionTable];
    newTable[index][field] = value;
    setEvolutionTable(newTable);
  };

  const handleSaveEvolution = async () => {
    setSavingEvolution(true);
    try {
      await axios.put(`${API_URL}/api/admin/patients/${patientId}/evolution`, {
        evolution_table: evolutionTable
      });
      toast.success('Tabela de evolução salva!');
    } catch (error) {
      console.error('Erro ao salvar evolução:', error);
      toast.error('Erro ao salvar tabela de evolução');
    } finally {
      setSavingEvolution(false);
    }
  };

  const handleCancel = () => {
    setEditData(patientData.patient);
    setPreviewFoto(null);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  if (!patientData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Paciente não encontrado</p>
        </div>
      </Layout>
    );
  }

  const patient = patientData.patient;
  const displayPhoto = previewFoto || patient.foto_perfil;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-button"
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Perfil do Paciente
            </h2>
            <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {isEditing ? 'Modo de edição' : 'Visualização completa dos dados'}
            </p>
          </div>
          
          {/* Botão Editar/Salvar */}
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-6 py-3 bg-[#0F2B4C] text-white rounded-full hover:shadow-lg transition-all font-medium"
            >
              <Edit3 size={20} />
              Editar Perfil
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-muted text-muted-foreground rounded-full hover:bg-muted/80 transition-all"
              >
                <X size={18} />
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-[#0F2B4C] text-white rounded-full hover:shadow-lg transition-all font-medium disabled:opacity-50"
              >
                <Save size={20} />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          )}
        </div>

        {/* Profile Header */}
        <div className="bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] rounded-2xl p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-4 border-white/30">
                {displayPhoto ? (
                  <img src={displayPhoto} alt={patient.nome} className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-20 h-20 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                )}
              </div>
              {isEditing && (
                <label className="absolute bottom-0 right-0 w-10 h-10 bg-white rounded-full flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow">
                  <Camera className="text-[#0F2B4C]" size={20} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              )}
              {!isEditing && (
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white"></div>
              )}
            </div>
            <div>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.nome || ''}
                  onChange={(e) => setEditData({ ...editData, nome: e.target.value })}
                  className="text-3xl font-bold mb-2 bg-white/20 rounded-lg px-3 py-1 text-white placeholder-white/70 border border-white/30 focus:outline-none focus:border-white"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                  placeholder="Nome do paciente"
                />
              ) : (
                <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient.nome}
                </h3>
              )}
              <p className="text-white/90 text-lg">
                Paciente • Cadastrado em {formatDateBR(patient.created_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Ações Rápidas - Movidas para cima */}
        {!isEditing && (
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => navigate(`/admin/paciente/${patientId}`)}
              className="bg-[#0F2B4C] text-white rounded-xl p-3 sm:p-4 text-left hover:shadow-lg transition-shadow flex items-center gap-3"
            >
              <TrendingUp size={20} className="flex-shrink-0" />
              <div>
                <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Ver Progresso
                </h3>
                <p className="text-xs opacity-90 hidden sm:block">Gráficos</p>
              </div>
            </button>

            <button
              onClick={() => navigate(`/admin/paciente/${patientId}/receitas`)}
              className="bg-[#B8977A] text-white rounded-xl p-3 sm:p-4 text-left hover:shadow-lg transition-shadow flex items-center gap-3"
            >
              <FileText size={20} className="flex-shrink-0" />
              <div>
                <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Receitas
                </h3>
                <p className="text-xs opacity-90 hidden sm:block">Prescrições</p>
              </div>
            </button>

            <button
              onClick={() => navigate(`/admin/paciente/${patientId}/plano`)}
              className="bg-gradient-to-br from-[#0F2B4C] to-[#B8977A] text-white rounded-xl p-3 sm:p-4 text-left hover:shadow-lg transition-shadow flex items-center gap-3"
            >
              <User size={20} className="flex-shrink-0" />
              <div>
                <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Plano Alimentar
                </h3>
                <p className="text-xs opacity-90 hidden sm:block">Dieta</p>
              </div>
            </button>
          </div>
        )}

        {/* Dados Pessoais */}
        <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
          <h3 className="text-xl font-bold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Informações Pessoais
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
              <Mail className="text-[#0F2B4C]" size={24} />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                {isEditing ? (
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none"
                    placeholder="email@exemplo.com"
                  />
                ) : (
                  <p className="font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {patient.email}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
              <Phone className="text-[#B8977A]" size={24} />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Telefone</p>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editData.telefone || ''}
                    onChange={(e) => setEditData({ ...editData, telefone: e.target.value })}
                    className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none"
                    placeholder="(00) 00000-0000"
                  />
                ) : (
                  <p className="font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {patient.telefone || 'Não informado'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
              <Calendar className="text-[#0F2B4C]" size={24} />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Data de Nascimento</p>
                {isEditing ? (
                  <input
                    type="date"
                    value={editData.data_nascimento || ''}
                    onChange={(e) => setEditData({ ...editData, data_nascimento: e.target.value })}
                    className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none"
                  />
                ) : (
                  <p className="font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {patient.data_nascimento ? formatDateBR(patient.data_nascimento) : 'Não informado'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dados de Saúde */}
        <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
          <h3 className="text-xl font-bold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dados de Saúde
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-muted rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Target className="text-[#0F2B4C]" size={20} />
                <p className="text-xs text-muted-foreground">Peso Inicial</p>
              </div>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.peso_inicial || ''}
                  onChange={(e) => setEditData({ ...editData, peso_inicial: e.target.value })}
                  className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none text-xl font-bold"
                  placeholder="85.0"
                />
              ) : (
                <p className="text-2xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient.peso_inicial || '--'} <span className="text-sm font-normal">kg</span>
                </p>
              )}
            </div>

            <div className="p-4 bg-[#0F2B4C]/10 rounded-xl border border-[#0F2B4C]/20">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="text-[#0F2B4C]" size={20} />
                <p className="text-xs text-[#0F2B4C] font-medium">Peso Atual</p>
              </div>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.peso_atual || ''}
                  onChange={(e) => setEditData({ ...editData, peso_atual: e.target.value })}
                  className="w-full bg-white rounded-lg px-3 py-2 border border-[#0F2B4C]/30 focus:border-[#0F2B4C] focus:outline-none text-xl font-bold text-[#0F2B4C]"
                  placeholder="80.0"
                />
              ) : (
                <p className="text-2xl font-bold text-[#0F2B4C]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient.peso_atual || '--'} <span className="text-sm font-normal">kg</span>
                </p>
              )}
            </div>

            <div className="p-4 bg-[#B8977A]/10 rounded-xl border border-[#B8977A]/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="text-[#B8977A]" size={20} />
                <p className="text-xs text-[#B8977A] font-medium">Meta de Peso</p>
              </div>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.peso_meta || ''}
                  onChange={(e) => setEditData({ ...editData, peso_meta: e.target.value })}
                  className="w-full bg-white rounded-lg px-3 py-2 border border-[#B8977A]/30 focus:border-[#B8977A] focus:outline-none text-xl font-bold text-[#B8977A]"
                  placeholder="70.0"
                />
              ) : (
                <p className="text-2xl font-bold text-[#B8977A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient.peso_meta || '--'} <span className="text-sm font-normal">kg</span>
                </p>
              )}
            </div>

            <div className="p-4 bg-muted rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Ruler className="text-[#B8977A]" size={20} />
                <p className="text-xs text-muted-foreground">Altura</p>
              </div>
              {isEditing ? (
                <input
                  type="number"
                  step="0.01"
                  value={editData.altura || ''}
                  onChange={(e) => setEditData({ ...editData, altura: e.target.value })}
                  className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none text-xl font-bold"
                  placeholder="1.70"
                />
              ) : (
                <p className="text-2xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient.altura || '--'} <span className="text-sm font-normal">m</span>
                </p>
              )}
            </div>

            <div className="p-4 bg-muted rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-[#0F2B4C]" size={20} />
                <p className="text-xs text-muted-foreground">Duração Trat.</p>
              </div>
              {isEditing ? (
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={editData.duracao_tratamento || ''}
                  onChange={(e) => setEditData({ ...editData, duracao_tratamento: e.target.value })}
                  className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none text-xl font-bold"
                  placeholder="6"
                />
              ) : (
                <p className="text-2xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient.duracao_tratamento || '--'} <span className="text-sm font-normal">meses</span>
                </p>
              )}
            </div>
          </div>

          {/* Histórico Médico */}
          <div className="mt-4 p-4 bg-muted rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="text-[#0F2B4C]" size={20} />
              <p className="text-xs text-muted-foreground font-medium">Histórico Médico</p>
            </div>
            {isEditing ? (
              <textarea
                value={editData.historico_medico || ''}
                onChange={(e) => setEditData({ ...editData, historico_medico: e.target.value })}
                className="w-full bg-white rounded-lg px-3 py-2 border border-border focus:border-[#B8977A] focus:outline-none min-h-[100px]"
                placeholder="Histórico médico, alergias, medicações em uso..."
              />
            ) : (
              <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {patient.historico_medico || 'Nenhum histórico registrado'}
              </p>
            )}
          </div>
        </div>

        {/* Medidas Iniciais */}
        <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
          <h3 className="text-xl font-bold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Medidas Iniciais (cm)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-xs text-muted-foreground mb-2">Cintura</p>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.cintura_inicial || ''}
                  onChange={(e) => setEditData({ ...editData, cintura_inicial: e.target.value })}
                  className="w-full bg-white rounded-lg px-2 py-1 border border-border focus:border-[#B8977A] focus:outline-none text-center font-bold"
                  placeholder="90"
                />
              ) : (
                <p className="text-xl font-bold text-highlight">{patient.cintura_inicial || '--'}</p>
              )}
            </div>
            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-xs text-muted-foreground mb-2">Quadril</p>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.quadril_inicial || ''}
                  onChange={(e) => setEditData({ ...editData, quadril_inicial: e.target.value })}
                  className="w-full bg-white rounded-lg px-2 py-1 border border-border focus:border-[#B8977A] focus:outline-none text-center font-bold"
                  placeholder="100"
                />
              ) : (
                <p className="text-xl font-bold text-highlight">{patient.quadril_inicial || '--'}</p>
              )}
            </div>
            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-xs text-muted-foreground mb-2">Peito</p>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.peito_inicial || ''}
                  onChange={(e) => setEditData({ ...editData, peito_inicial: e.target.value })}
                  className="w-full bg-white rounded-lg px-2 py-1 border border-border focus:border-[#B8977A] focus:outline-none text-center font-bold"
                  placeholder="95"
                />
              ) : (
                <p className="text-xl font-bold text-highlight">{patient.peito_inicial || '--'}</p>
              )}
            </div>
            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-xs text-muted-foreground mb-2">Braço</p>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.braco_inicial || ''}
                  onChange={(e) => setEditData({ ...editData, braco_inicial: e.target.value })}
                  className="w-full bg-white rounded-lg px-2 py-1 border border-border focus:border-[#B8977A] focus:outline-none text-center font-bold"
                  placeholder="35"
                />
              ) : (
                <p className="text-xl font-bold text-highlight">{patient.braco_inicial || '--'}</p>
              )}
            </div>
            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-xs text-muted-foreground mb-2">Perna</p>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editData.perna_inicial || ''}
                  onChange={(e) => setEditData({ ...editData, perna_inicial: e.target.value })}
                  className="w-full bg-white rounded-lg px-2 py-1 border border-border focus:border-[#B8977A] focus:outline-none text-center font-bold"
                  placeholder="60"
                />
              ) : (
                <p className="text-xl font-bold text-highlight">{patient.perna_inicial || '--'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Tabela de Evolução / Aplicação */}
        <div className="bg-card rounded-2xl shadow-sm border border-border p-4 sm:p-6 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg sm:text-xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Tabela de Evolução / Aplicação
            </h3>
            <button
              onClick={handleSaveEvolution}
              disabled={savingEvolution}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-[#0F2B4C] text-white rounded-full hover:shadow-lg transition-all text-sm font-medium disabled:opacity-50 w-full sm:w-auto"
            >
              <Save size={16} />
              {savingEvolution ? 'Salvando...' : 'Salvar Tabela'}
            </button>
          </div>
          
          <div className="w-full overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            <table className="border-collapse" style={{ minWidth: 'max-content' }}>
              <thead>
                <tr>
                  <th className="bg-[#B8977A] text-white p-3 text-left text-sm font-semibold rounded-tl-xl whitespace-nowrap">
                    Dados
                  </th>
                  {evolutionTable.map((item, index) => (
                    <th key={index} className={`bg-[#B8977A] text-white p-3 text-center text-sm font-semibold whitespace-nowrap ${index === evolutionTable.length - 1 ? 'rounded-tr-xl' : ''}`}>
                      {index + 1}º Dose
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="bg-muted p-3 text-sm font-medium text-highlight whitespace-nowrap">Doses Mg</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-2 text-center">
                      <input
                        type="text"
                        value={item.dose_mg}
                        onChange={(e) => handleEvolutionChange(index, 'dose_mg', e.target.value)}
                        className="w-full min-w-[80px] bg-muted border border-border rounded-lg px-2 py-1.5 text-center text-sm focus:border-[#B8977A] focus:outline-none"
                        placeholder="--"
                      />
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="bg-muted p-3 text-sm font-medium text-highlight whitespace-nowrap">Data Aplicação</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-2 text-center">
                      <input
                        type="date"
                        value={item.data_aplicacao}
                        onChange={(e) => handleEvolutionChange(index, 'data_aplicacao', e.target.value)}
                        className="w-full min-w-[120px] bg-muted border border-border rounded-lg px-2 py-1.5 text-center text-sm focus:border-[#B8977A] focus:outline-none"
                      />
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="bg-muted p-3 text-sm font-medium text-highlight whitespace-nowrap">Peso (Kg)</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-2 text-center">
                      <input
                        type="number"
                        step="0.1"
                        value={item.peso}
                        onChange={(e) => handleEvolutionChange(index, 'peso', e.target.value)}
                        className="w-full min-w-[80px] bg-muted border border-border rounded-lg px-2 py-1.5 text-center text-sm focus:border-[#B8977A] focus:outline-none"
                        placeholder="--"
                      />
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="bg-muted p-3 text-sm font-medium text-highlight whitespace-nowrap">Med. Quadril</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className="p-2 text-center">
                      <input
                        type="number"
                        step="0.1"
                        value={item.med_quadril}
                        onChange={(e) => handleEvolutionChange(index, 'med_quadril', e.target.value)}
                        className="w-full min-w-[80px] bg-muted border border-border rounded-lg px-2 py-1.5 text-center text-sm focus:border-[#B8977A] focus:outline-none"
                        placeholder="--"
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="bg-muted p-3 text-sm font-medium text-highlight whitespace-nowrap rounded-bl-xl">Med. Abdome</td>
                  {evolutionTable.map((item, index) => (
                    <td key={index} className={`p-2 text-center ${index === evolutionTable.length - 1 ? '' : ''}`}>
                      <input
                        type="number"
                        step="0.1"
                        value={item.med_abdome}
                        onChange={(e) => handleEvolutionChange(index, 'med_abdome', e.target.value)}
                        className="w-full min-w-[80px] bg-muted border border-border rounded-lg px-2 py-1.5 text-center text-sm focus:border-[#B8977A] focus:outline-none"
                        placeholder="--"
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            * A tabela se adapta automaticamente à duração do tratamento ({patient.duracao_tratamento || 6} meses × 4 doses = {(patient.duracao_tratamento || 6) * 4} doses). Deslize horizontalmente para ver todas as doses.
          </p>
        </div>

        {/* Resumo de Atividade */}
        <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
          <h3 className="text-xl font-bold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Resumo de Atividade
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-3xl font-bold text-highlight mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {patientData.recent_progress?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">Registros de Progresso</p>
            </div>

            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-3xl font-bold text-highlight mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {patientData.prescriptions?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">Prescrições</p>
            </div>

            <div className="p-4 bg-muted rounded-xl text-center">
              <p className="text-3xl font-bold text-highlight mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {patientData.meal_plans?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">Planos Alimentares</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminPerfilPaciente;
